import {
    ConditionalRender,
    eventBus,
    isFailed,
    isFailedByOffline,
    isShowLoader,
    selectError,
    selectMutableWidget,
    selectScenarioId,
    selectStatus,
    useApp,
    useAppSelector,
    useAppStore,
    useNavigationBlockerViaHistory,
    useOnline,
    useSystemAction,
    useTranslation,
    useTriggerAction,
} from '@libs/core';
import { ActionSystemActionType, AssetsSvgMap, EStatus, SystemEventTypeEnum, TCommonData, TWidget } from '@libs/types';
import React, { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { CommonScreen } from './common/CommonScreen/CommonScreen';
import { buildOfflineToast, OFFLINE_MESSAGE_FALLBACK, OFFLINE_MESSAGE_KEY } from './common/Offline';
import OfflinePopup from './common/OfflinePopup';
import { useSlowRequestGuard } from './hooks/useSlowRequestGuard';
import { TranslationKeys } from './types/enums';
import styled from './widgetWrapper.module.css';

const LOADER_DELAY = 800;
const EMPTY_WIDGET = {} as TWidget<TCommonData>;

export const WidgetWrapper = ({ children }: { children: React.ReactNode }) => {
    const isLoader = useAppSelector(isShowLoader);
    const isOnline = useOnline();
    const isStatusFlailed = useAppSelector(isFailed);
    const isOfflineFailure = useAppSelector(isFailedByOffline);
    const status = useAppSelector(selectStatus);
    const scenarioId = useAppSelector(selectScenarioId);
    const navigate = useNavigate();
    const { setPopup } = useApp();

    const { triggerAction } = useTriggerAction(EMPTY_WIDGET, true);
    const { t } = useTranslation();
    const systemActionNavigateBack = useSystemAction(ActionSystemActionType.navigateBack);
    useNavigationBlockerViaHistory(true, { triggerAction, systemActionNavigateBack });
    const store = useAppStore();

    /**
     * Cold start without a connection.
     *
     * The root loader only dispatches `fetchScenario` when `navigator.onLine` is true, so an app
     * started offline never produces a rejected request: the store stays untouched and `isFailed`
     * is false. `EStatus.Idle` together with an empty `scenarioId` means no scenario was ever
     * loaded — the only state the player cannot return to once the first request has been sent
     * (pending switches the status to Loading / WidgetLoading right away).
     */
    const isColdStartOffline = status === EStatus.Idle && !scenarioId;
    // Whether the offline popup was opened for a cold start, i.e. there is no scenario to fall back to
    // once the connection returns. Guards the auto-recovery below from firing on a normal online start.
    const isColdStartPopupShown = useRef(false);

    const handleRetry = useCallback(() => {
        if (!navigator.onLine) {
            // Still no connection: keep the popup open and give feedback with the offline toast.
            eventBus.emitGeneral(SystemEventTypeEnum.TOAST_MESSAGE, buildOfflineToast(t(OFFLINE_MESSAGE_KEY, OFFLINE_MESSAGE_FALLBACK)));
            return;
        }

        const state = store.getState();
        const error = selectError(state);
        setPopup(null);

        if (!error?.actionForRepeat) {
            // Nothing to replay (e.g. the initial scenario request failed) — restart the player.
            window.location.reload();
            return;
        }

        const widget = error.widgetUid ? selectMutableWidget(state, error.widgetUid) : undefined;
        triggerAction(error.actionForRepeat, {
            widgetAction: widget as TWidget<TCommonData> | undefined,
            isShowLoader: true,
            // Without the originating widget a widget-scoped request cannot be rebuilt, so replay it as a scenario one.
            forceScenario: !widget,
        }).catch((e) => console.error('Offline retry failed:', e));
    }, [setPopup, store, t, triggerAction]);

    // Toast, then offline / slow-service popup for a request that keeps hanging on the wire.
    useSlowRequestGuard({ isOnline, onRetry: handleRetry });

    useEffect(() => {
        if (isLoader) {
            // @ts-expect-error
            document?.activeElement?.blur?.();
        }
        if (!isOnline && (isStatusFlailed || isColdStartOffline)) {
            isColdStartPopupShown.current = isColdStartOffline;
            setPopup(<OfflinePopup variant="offline" onRetry={handleRetry} />);
        }
    }, [handleRetry, isColdStartOffline, isLoader, isOnline, isStatusFlailed, setPopup]);

    useEffect(() => {
        // The connection is back after a cold start: nothing was ever rendered behind the popup and the
        // loader will not run again on its own, so restart the player instead of leaving an empty screen.
        if (isOnline && isColdStartPopupShown.current && isColdStartOffline) {
            isColdStartPopupShown.current = false;
            setPopup(null);
            window.location.reload();
        }
    }, [isColdStartOffline, isOnline, setPopup]);

    return (
        <>
            {/* A failure caused by a lost connection is handled by the offline flow (toast + popup), so the generic error screen stays hidden even once the network is back. */}
            <ConditionalRender condition={isOnline && isStatusFlailed && !isOfflineFailure}>
                <CommonScreen
                    useIcon={false}
                    fill="pink_100"
                    iconName={AssetsSvgMap.ic_x_circle_thin_80}
                    mainText={t(TranslationKeys.GeneralErrorText, 'Something went wrong...')}
                    buttonLabel={t(TranslationKeys.ButtonRetry, 'Try again')}
                    buttonHandleClick={() => {
                        navigate('/clearSession');
                    }}
                />
            </ConditionalRender>
            <ConditionalRender condition={isLoader}>
                <div className={styled.overlayContainer}>
                    <ConditionalRender condition={isLoader} delay={LOADER_DELAY}>
                        <CommonScreen useBackground={false} useSpinner />
                    </ConditionalRender>
                </div>
            </ConditionalRender>
            {children}
        </>
    );
};
